import React, { useMemo, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import Carousel from 'react-multi-carousel';
import cc from 'classcat';

import placeholderImage from '../../images/placeholder.gif';

import styles from './styles.module.scss';

interface Props {
  photos: AssetFragment[];
  showThumbnails?: boolean;
}

const PhotoCarousel = ({ photos, showThumbnails = false }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<Carousel>(null);
  const thumbnailWrapperRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const mains = useMemo(
    () =>
      photos.map((p: AssetFragment) => (
        <div key={`photo-${p.url}`}>
          <img src={p.url!} alt={p.title!} />
        </div>
      )),
    [photos],
  );

  const responsive = {
    all: {
      breakpoint: { min: 0, max: 10000 },
      items: 1,
    },
  };

  const carouselProps = {
    infinite: false,
    responsive,
    autoPlay: false,
    shouldResetAutoplay: false,
    ref: carouselRef,
    afterChange: (_previousSlide: number, { currentSlide: nextSlide }: { currentSlide: number }) => {
      setCurrentSlide(nextSlide);
    },
  };

  const handleThumbnailClick = (index: number) => {
    if (carouselRef.current && !isDragging) {
      carouselRef.current.goToSlide(index);
      setCurrentSlide(index);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!thumbnailWrapperRef.current) return;
    e.preventDefault();
    setIsMouseDown(true);
    setIsDragging(false);
    setStartX(e.pageX - thumbnailWrapperRef.current.offsetLeft);
    setScrollLeft(thumbnailWrapperRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown || !thumbnailWrapperRef.current) return;
    e.preventDefault();
    const x = e.pageX - thumbnailWrapperRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    if (Math.abs(walk) > 5) {
      setIsDragging(true);
    }
    thumbnailWrapperRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    setTimeout(() => setIsDragging(false), 100);
  };

  const handleMouseLeave = () => {
    setIsMouseDown(false);
    setIsDragging(false);
  };

  if (!photos.length) {
    return <img src={placeholderImage.src} height={565} width={835} alt="" />;
  }

  if (photos.length === 1) {
    return <img src={photos[0].url!} height={565} width={835} alt={photos[0].url!} />;
  }

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.carousel}>
        <div>
          <div className={styles.slider}>
            <Carousel {...carouselProps}>{mains}</Carousel>
          </div>
        </div>
      </div>

      {showThumbnails && photos.length > 1 && (
        <div
          ref={thumbnailWrapperRef}
          className={styles.thumbnailWrapper}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {photos.map((photo, index) => (
            <button
              key={`thumb-${photo.url}`}
              className={cc([styles.thumbnail, { [styles.active]: currentSlide === index }])}
              onClick={() => handleThumbnailClick(index)}
              type="button"
              aria-label={`View photo ${index + 1}`}
            >
              <img src={photo.url!} alt={photo.title || `Photo ${index + 1}`} draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default observer(PhotoCarousel);
